import React from "react";

export const Typography: React.FC = () => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const textSamples = [
    {
      label: "Large Display",
      style: { fontSize: "2.25rem", fontWeight: 600, lineHeight: 1.2 },
      text: "Welcome Screen Heading",
      specs: "36px / 600",
    },
    {
      label: "Dialog Title",
      style: { fontSize: "1.313rem", fontWeight: 700, lineHeight: 1.3 },
      text: "Export Options",
      specs: "21px / 700",
    },
    {
      label: "Section Header",
      style: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3 },
      text: "Library Components",
      specs: "20px / 600",
    },
    {
      label: "Subsection",
      style: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 },
      text: "Help Topics",
      specs: "18px / 600",
    },
    {
      label: "Body Large",
      style: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.5 },
      text: "Standard interface text for buttons, menus, and primary content areas.",
      specs: "16px / 400",
    },
    {
      label: "Body Medium",
      style: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.5 },
      text: "Common text size for dialog content, form labels, and secondary information.",
      specs: "14px / 400",
    },
    {
      label: "Body Small",
      style: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.4 },
      text: "Property panel labels, tooltips, and compact interface elements.",
      specs: "12px / 400",
    },
    {
      label: "Caption",
      style: {
        fontSize: "0.625rem",
        fontWeight: 400,
        lineHeight: 1.3,
        color: "var(--color-gray-60)",
      },
      text: "Very small text for badges, status indicators, and metadata.",
      specs: "10px / 400",
    },
    {
      label: "Tiny",
      style: {
        fontSize: "0.5rem",
        fontWeight: 600,
        lineHeight: 1.2,
        color: "var(--color-gray-60)",
      },
      text: "Minimal text for micro-interactions and compact badges.",
      specs: "8px / 600",
    },
  ];

  const primaryFonts = [
    {
      name: "Assistant (UI Font)",
      usage: "Primary Interface",
      family: 'var(--ui-font, "Assistant", sans-serif)',
      samples: [
        {
          text: "Main interface text, buttons, and labels",
          size: "1rem",
          weight: 400,
        },
        {
          text: "Medium weight for emphasis",
          size: "1rem",
          weight: 500,
        },
        {
          text: "Bold headings and important text",
          size: "1.125rem",
          weight: 600,
        },
      ],
      css: 'font-family: var(--ui-font, "Assistant", sans-serif);',
    },
    {
      name: "Cascadia Code",
      usage: "Monospace/Code",
      family: '"Cascadia", "Cascadia Code", "Fira Code", "Monaco", monospace',
      samples: [
        {
          text: "Code snippets and technical content",
          size: "0.875rem",
          weight: 400,
        },
        {
          text: "Used for code blocks, technical data, and monospace content",
          size: "0.875rem",
          weight: 400,
        },
      ],
      css: 'font-family: "Cascadia", "Cascadia Code", "Fira Code", "Monaco", monospace;',
    },
    {
      name: "Excalifont",
      usage: "Welcome Screen",
      family: '"Excalifont", "Xiaolai", "Comic Sans MS", cursive',
      samples: [
        {
          text: "Special handwritten font for welcome screen",
          size: "1.5rem",
          weight: 400,
        },
      ],
      css: 'font-family: "Excalifont", "Xiaolai", "Comic Sans MS", cursive;',
    },
  ];

  const canvasFonts = [
    {
      name: "Virgil",
      usage: "Regular",
      family: '"Virgil", "Kalam", "Comic Sans MS", sans-serif',
      samples: [
        { text: "Regular font for drawing", size: "1.125rem", weight: 400 },
      ],
      css: 'font-family: "Virgil", "Kalam", "Comic Sans MS", sans-serif;',
    },
    {
      name: "Comic Shanns",
      usage: "Regular",
      family: '"Comic Shanns", "Comic Sans MS", cursive',
      samples: [
        { text: "Comic font for drawing", size: "1.5rem", weight: 400 },
      ],
      css: 'font-family: "Comic Shanns", "Comic Sans MS", cursive;',
    },
    {
      name: "Nunito",
      usage: "Regular",
      family: '"Nunito", "Open Sans", sans-serif',
      samples: [
        { text: "Sans-serif font for drawing", size: "0.875rem", weight: 400 },
      ],
      css: 'font-family: "Nunito", "Open Sans", sans-serif;',
    },
    {
      name: "Lilita One",
      usage: "Display",
      family: '"Lilita One", "Impact", cursive',
      samples: [
        { text: "Bold display font for drawing", size: "1.25rem", weight: 400 },
      ],
      css: 'font-family: "Lilita One", "Impact", cursive;',
    },
    {
      name: "Liberation Sans",
      usage: "System",
      family: '"Liberation Sans", "Arial", sans-serif',
      samples: [
        { text: "System font for PDF/PNG export", size: "1rem", weight: 400 },
      ],
      css: 'font-family: "Liberation Sans", "Arial", sans-serif;',
    },
    {
      name: "Helvetica",
      usage: "System",
      family: '"Helvetica", "Arial", sans-serif',
      samples: [
        {
          text: "System font (macOS: Helvetica, Windows: Arial)",
          size: "1rem",
          weight: 400,
        },
      ],
      css: 'font-family: "Helvetica", "Arial", sans-serif;',
    },
    {
      name: "Xiaolai",
      usage: "CJK Fallback",
      family: '"Xiaolai", "SimSun", serif',
      samples: [
        {
          text: "手绘风格中文字体 (CJK hand-drawn style font)",
          size: "1rem",
          weight: 400,
        },
      ],
      css: 'font-family: "Xiaolai", "SimSun", serif;',
    },
  ];

  const fontUsageGuide = [
    {
      font: "Virgil",
      description:
        "Default handwritten style, perfect for sketches and casual diagrams",
      color: "#007bff",
    },
    {
      font: "Excalifont",
      description: "Enhanced handwritten style with better character support",
      color: "#28a745",
    },
    {
      font: "Comic Shanns",
      description: "Playful comic-style font for fun diagrams",
      color: "#ffc107",
    },
    {
      font: "Nunito",
      description: "Clean sans-serif for professional diagrams",
      color: "#17a2b8",
    },
    {
      font: "Lilita One",
      description: "Bold display font for titles and emphasis",
      color: "#dc3545",
    },
  ];

  return (
    <div>
      <div className="ds-section-header">
        <h2>📝 Typography System</h2>
        <p className="ds-description">
          Font families, text scales, and typography guidelines for Excalidraw
        </p>
      </div>

      <div className="typography-content">
        {/* Text Scale */}
        <div className="typography-section">
          <h3 className="ds-section-title">Text Scale</h3>

          <div className="typography-showcase">
            <div className="text-scale">
              {textSamples.map((sample, index) => (
                <div key={index} className="text-sample">
                  <div className="text-label">{sample.label}</div>
                  <div className="text-example" style={sample.style}>
                    {sample.text}
                  </div>
                  <div className="text-specs">{sample.specs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Font Families */}
        <div className="typography-section">
          <h3 className="ds-section-title">Font Families</h3>

          <div className="font-family-section">
            {primaryFonts.map((font, index) => (
              <div key={index} className="font-sample">
                <div className="font-sample-header">
                  <span className="font-name">{font.name}</span>
                  <span className="font-usage">{font.usage}</span>
                </div>
                {font.samples.map((sample, sampleIndex) => (
                  <div
                    key={sampleIndex}
                    className="font-preview"
                    style={{
                      fontFamily: font.family,
                      fontSize: sample.size,
                      fontWeight: sample.weight,
                    }}
                  >
                    {sample.text}
                  </div>
                ))}
                <div
                  className="font-css"
                  onClick={() => copyToClipboard(font.css)}
                  title="Click to copy"
                >
                  {font.css}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas Drawing Fonts */}
        <div className="typography-section">
          <h3 className="ds-section-title">Canvas Drawing Fonts</h3>

          <div className="font-family-section">
            {canvasFonts.map((font, index) => (
              <div key={index} className="font-sample">
                <div className="font-sample-header">
                  <span className="font-name">{font.name}</span>
                  <span className="font-usage">{font.usage}</span>
                </div>
                {font.samples.map((sample, sampleIndex) => (
                  <div
                    key={sampleIndex}
                    className="font-preview"
                    style={{
                      fontFamily: font.family,
                      fontSize: sample.size,
                      fontWeight: sample.weight,
                    }}
                  >
                    {sample.text}
                  </div>
                ))}
                <div
                  className="font-css"
                  onClick={() => copyToClipboard(font.css)}
                  title="Click to copy"
                >
                  {font.css}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Font Usage Guide */}
        <div className="typography-section">
          <h3 className="ds-section-title">Font Usage Guide</h3>

          <div className="font-usage-grid">
            {fontUsageGuide.map((guide, index) => (
              <div key={index} className="font-usage-card">
                <div
                  className="font-usage-header"
                  style={{ borderColor: guide.color }}
                >
                  <h4 style={{ color: guide.color }}>{guide.font}</h4>
                </div>
                <p className="font-usage-description">{guide.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
